import React, { useEffect, useMemo } from 'react';

import { Box } from '@mui/material';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';

import { NameInput } from '@/features/Dashboard/components/NameInput';
import { NAME_MAX_LENGTH } from '@/features/Dashboard/constants/const';
import { createNameSchema } from '@/features/Dashboard/utils/validateName';
import { useDashboard } from '@/hooks/api/dashboard';
import { useAppSelector } from '@/hooks/redux-hooks';

import { useDashboardEditActions } from '../../hooks/useDashboardEditActions';
import {
  selectDashboardName,
  selectInitialDashboardName
} from '../../model/dashboardEdit.selectors';

import type { StandardSchemaV1Issue } from '@tanstack/react-form';

const convertErrorsToStandardSchema = (
  errors: StandardSchemaV1Issue[]
): StandardSchemaV1Issue[] | undefined => {
  return errors.length > 0 ? errors : undefined;
};

interface EditDashboardNameProps {
  onValidationChange?: (hasErrors: boolean) => void;
}

const EditDashboardNameFieldContent = ({
  field,
  onValidationChange,
  setDashboardName
}: {
  field: any;
  onValidationChange?: (hasErrors: boolean) => void;
  setDashboardName: (name: string) => void;
}) => {
  const hasErrors = field.state.meta.errors.length > 0;

  useEffect(() => {
    onValidationChange?.(hasErrors);
  }, [hasErrors, onValidationChange]);

  return (
    <Box
      sx={{
        position: 'relative',
        maxWidth: '255px',
        '& .MuiInputBase-root': {
          fontSize: '32px',
          lineHeight: '32px',
          fontWeight: 700
        },
        '& .MuiInputBase-input': {
          fontSize: '32px',
          lineHeight: '32px',
          fontWeight: 700,
          padding: '7px 2px 7px 8px'
        }
      }}
    >
      <NameInput
        error={convertErrorsToStandardSchema(field.state.meta.errors as StandardSchemaV1Issue[])}
        maxLength={NAME_MAX_LENGTH}
        value={field.state.value}
        onBlur={() => {
          field.handleBlur();
          if (field.state.meta.errors.length === 0 && field.state.value.trim()) {
            const trimmed = field.state.value.trim();
            setDashboardName(trimmed);
          }
        }}
        onChange={(newValue: string) => {
          field.handleChange(newValue);
        }}
      />
    </Box>
  );
};

const EditDashboardName = ({ onValidationChange }: EditDashboardNameProps) => {
  const dashboardName = useAppSelector(selectDashboardName);
  const initialDashboardName = useAppSelector(selectInitialDashboardName);
  const { renameDashboard } = useDashboardEditActions();
  const { data: dashboardData } = useDashboard();

  // Get existing dashboard names (excluding the current dashboard being edited)
  const existingNames = useMemo(() => {
    if (!dashboardData) return [];
    return Object.keys(dashboardData)
      .filter(name => name !== initialDashboardName)
      .map(name => name.toLowerCase());
  }, [dashboardData, initialDashboardName]);

  // Create schema with existing names for validation
  const DashboardNameSchema = useMemo(
    () =>
      z.object({
        name: createNameSchema('Dashboard name', NAME_MAX_LENGTH, existingNames)
      }),
    [existingNames]
  );

  const form = useForm({
    defaultValues: {
      name: dashboardName ?? ''
    },
    validators: {
      onChange: DashboardNameSchema
    }
  });

  // Synchronize form with Redux state when it's initialized or changed from outside
  useEffect(() => {
    if (dashboardName && !form.state.isDirty && form.state.values.name !== dashboardName) {
      form.reset({ name: dashboardName });
    }
  }, [dashboardName, form]);

  return (
    <form.Field name='name'>
      {field => (
        <EditDashboardNameFieldContent
          field={field}
          setDashboardName={name => renameDashboard(name)}
          onValidationChange={onValidationChange}
        />
      )}
    </form.Field>
  );
};

export default EditDashboardName;
